import React, { useState, useRef, useEffect, useCallback } from "react";
import { RiSendPlaneFill } from "react-icons/ri";
import { RxCross2 } from "react-icons/rx";
import { FiUser, FiCpu } from "react-icons/fi";

const ChatComponent = ({ isVisible, onClose, selectedTab, chatHeight, setChatHeight }) => {
  const [chatMessage, setChatMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Use refs to track drag state to avoid stale closures
  const dragState = useRef({
    isDragging: false,
    startY: 0,
    startHeight: 0
  });

  // Static messages for demo
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Welcome to Staart AI! I'm here to help you with your project timeline. How can I assist you today?",
      sender: "ai",
      timestamp: new Date(Date.now() - 300000), // 5 minutes ago
    },
    {
      id: 2,
      text: "Hello! I need help understanding the project dependencies.",
      sender: "user",
      timestamp: new Date(Date.now() - 240000), // 4 minutes ago
    },
    {
      id: 3,
      text: "I'd be happy to help! Dependencies show which tasks need to be completed before others can start. In your timeline, you can see predecessor and successor columns that define these relationships. Would you like me to explain a specific dependency?",
      sender: "ai",
      timestamp: new Date(Date.now() - 180000), // 3 minutes ago
    },
    {
      id: 4,
      text: "That's helpful. Can you also explain how to adjust task durations?",
      sender: "user",
      timestamp: new Date(Date.now() - 120000), // 2 minutes ago
    },
    {
      id: 5,
      text: "Certainly! Task durations are automatically calculated based on start and end dates. You can modify these by clicking on the date fields in any task row. The system will recalculate dependencies and highlight any potential conflicts. Would you like guidance on any specific task?",
      sender: "ai",
      timestamp: new Date(Date.now() - 60000), // 1 minute ago
    }
  ]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle sending messages
  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (chatMessage.trim()) {
      const newMessage = {
        id: messages.length + 1,
        text: chatMessage,
        sender: "user",
        timestamp: new Date(),
      };
      setMessages([...messages, newMessage]);
      setChatMessage("");

      // Simulate AI response after a short delay
      setTimeout(() => {
        const aiResponse = {
          id: messages.length + 2,
          text: "Thank you for your message! I'm processing your request and will provide assistance based on your current " + selectedTab + " view. How else can I help you optimize your project timeline?",
          sender: "ai",
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, aiResponse]);
      }, 1500);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Drag handlers for resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragState.current.isDragging = true;
    dragState.current.startY = e.clientY;
    dragState.current.startHeight = chatHeight;
    
    setIsDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ns-resize";
  };

  // Stable event handlers using useCallback
  const handleMouseMove = useCallback((e) => {
    if (!dragState.current.isDragging) return;
    
    e.preventDefault();
    
    const deltaY = dragState.current.startY - e.clientY;
    const windowHeight = window.innerHeight;
    const newHeight = Math.min(90, Math.max(20, dragState.current.startHeight + (deltaY / windowHeight) * 100));
    
    setChatHeight(newHeight);
  }, [setChatHeight]);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    
    dragState.current.isDragging = false;
    setIsDragging(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  // Update useEffect to manage event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, []);

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isVisible) return null;

  return (
    <div
      ref={chatRef}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: `${chatHeight}vh`,
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e5e7eb",
        boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        transition: isDragging ? "none" : "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      {/* Resize Handle */}
      <div
        style={{
          height: "8px",
          background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
          cursor: "ns-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid #e5e7eb",
          position: "relative",
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)";
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.background = "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)";
          }
        }}
      >
        <div
          style={{
            width: "40px",
            height: "4px",
            borderRadius: "2px",
            backgroundColor: isDragging ? "#6366f1" : "#9ca3af",
            transition: "background-color 0.2s ease",
          }}
        />
        {/* Tooltip */}
        <div
          style={{
            position: "absolute",
            top: "-30px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#374151",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            opacity: isDragging ? 1 : 0,
            transition: "opacity 0.2s ease",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          Drag to resize • {Math.round(chatHeight)}% height
        </div>
      </div>

      {/* Chat Header */}
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#fafbfc",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
            }}
          >
            <FiCpu size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#111827" }}>
              Staart AI Assistant
            </h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              Available for {selectedTab} • Online
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: "8px",
            border: "none",
            background: "none",
            cursor: "pointer",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <RxCross2 size={20} color="#6b7280" />
        </button>
      </div>

      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 24px",
          backgroundColor: "#f9fafb",
        }}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              marginBottom: "16px",
              flexDirection: message.sender === "user" ? "row-reverse" : "row",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: message.sender === "user" 
                  ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                  : "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                flexShrink: 0,
              }}
            >
              {message.sender === "user" ? <FiUser size={16} /> : <FiCpu size={16} />}
            </div>
            <div
              style={{
                maxWidth: "70%",
                backgroundColor: message.sender === "user" ? "#e0f2fe" : "#ffffff",
                padding: "12px 16px",
                borderRadius: message.sender === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                border: message.sender === "user" ? "1px solid #b3e5fc" : "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <p style={{ 
                margin: 0, 
                fontSize: "14px", 
                lineHeight: "1.5",
                color: "#374151",
              }}>
                {message.text}
              </p>
              <p style={{ 
                margin: "8px 0 0 0", 
                fontSize: "11px", 
                color: "#9ca3af",
                textAlign: message.sender === "user" ? "right" : "left",
              }}>
                {formatTime(message.timestamp)}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        style={{
          padding: "16px 24px",
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
        }}
      >
        <form onSubmit={handleSendMessage} style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <textarea
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              rows={1}
              style={{
                width: "100%",
                padding: "12px 50px 12px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "24px",
                fontSize: "14px",
                outline: "none",
                resize: "none",
                transition: "border-color 0.2s ease",
                lineHeight: "1.5",
                maxHeight: "120px",
                minHeight: "48px",
                fontFamily: "inherit",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#6366f1";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#d1d5db";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <button
              type="submit"
              disabled={!chatMessage.trim()}
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                background: chatMessage.trim() 
                  ? "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)"
                  : "#e5e7eb",
                color: "#ffffff",
                cursor: chatMessage.trim() ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
            >
              <RiSendPlaneFill size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatComponent; 